/**
 * 消息操作 Composable
 * 负责消息的发送、编辑、删除、重新生成等操作
 */

import { ref, nextTick } from "vue";
import { ElMessage } from "element-plus/es/components/message";
import { ChatRole } from "~/enums";
import { useMessageSave } from "./useMessageSave";
import { normalizeChatErrorMessage } from "~/utils/chat-error";

export function useMessageOperations(
  sessionId: any,
  currentUserId: any,
  messages: any,
  input: any,
  handleSubmit: any,
  reload: any,
  stop: any,
  status: any,
  initSession: any,
  updateSessionTitle: any,
  clearThinkingSteps: any,
  pendingAttachments?: any, // 接收附件数据
  selectedModel?: any, // 接收模型选择
  selectedPromptTemplateKey?: any,
  pendingClientTraceId?: any,
  cacheActions?: {
    sync: () => void;
    clear: () => void;
    setActiveSession?: (sessionId: string) => void;
    notifySessionPersisted?: () => void;
  },
) {
  const { saveMessage } = useMessageSave();

  const editingMessageIndex = ref<number | null>(null); // 正在编辑的消息索引
  const editingContent = ref(""); // 编辑后的问题内容

  /**
   * 点击【发送】按钮发送消息
   * @param attachments - 附件列表（可选）
   */
  const handleSend = async (attachments?: any[]): Promise<boolean> => {
    const userText = input.value.trim();

    if ((!userText && (!attachments || attachments.length === 0)) || status.value === "streaming") {
      return false;
    }

    // 新问题不沿用上一轮步骤，避免抽屉展示过期的工具调用轨迹。
    clearThinkingSteps();
    if (pendingClientTraceId) {
      pendingClientTraceId.value = createClientTraceId();
    }

    if (!sessionId.value) {
      console.log("🆕 首次发送消息，创建新会话");
      // 必须先拿到会话 ID，再保存用户消息，避免历史记录落到临时会话。
      await initSession();
    }
    cacheActions?.setActiveSession?.(sessionId.value);

    const sortIndex = messages.value.length;

    // 构建消息内容（如果有附件，添加到内容中）
    let messageContent = userText;
    if (attachments && attachments.length > 0) {
      if (!messageContent) {
        // 纯附件也需要一段文本触发 SDK 发送，真实附件内容通过 pendingAttachments 传给后端。
        messageContent = `发送了 ${attachments.length} 个文件`;
      }

      // 设置待发送的附件数据
      if (pendingAttachments) {
        pendingAttachments.value = attachments;
        console.log(`📎 handleSend: 已设置 pendingAttachments, 数量=${attachments.length}`);
      }
    }

    const savedUserMessage = await saveMessage(
      sessionId.value,
      currentUserId.value,
      ChatRole.USER,
      messageContent,
      sortIndex,
      undefined,
      attachments,
    );

    if (sortIndex === 0) {
      // 首条消息先生成临时标题，后续 AI 摘要标题失败时列表也有可读名称。
      await updateSessionTitle(messageContent, { isTitleAutoGenerated: true });
      if (savedUserMessage?.id) {
        cacheActions?.notifySessionPersisted?.();
      }
    }

    try {
      // 调用 Vercel AI SDK 的 handleSubmit
      await handleSubmit();

      if (attachments && attachments.length > 0) {
        const lastMessage = messages.value[messages.value.length - 1];
        if (lastMessage && lastMessage.role === ChatRole.USER && !lastMessage.attachments) {
          // SDK 只保留文本消息，手动挂回附件用于当前界面预览和刷新前缓存。
          lastMessage.attachments = attachments;
        }
      }
      cacheActions?.sync();
    } catch (error) {
      console.warn("AI 回复失败，已作为助手消息展示:", normalizeChatErrorMessage(error));
    }

    input.value = "";

    // prepareRequestBody 读取的是共享 ref，延迟清空可避免请求体还没组装完成就丢失附件。
    setTimeout(() => {
      if (pendingAttachments) {
        pendingAttachments.value = [];
        console.log(`🗑️ handleSend: 已清空 pendingAttachments`);
      }
      if (selectedPromptTemplateKey) {
        selectedPromptTemplateKey.value = null;
      }
    }, 100);

    return true;
  };

  /**
   * 点击【停止】按钮停止生成本轮回复
   */
  const stopGeneration = () => {
    stop();
  };

  /**
   * 点击【新建会话】按钮新建会话
   */
  const handleNewChat = async () => {
    cacheActions?.clear();

    // 清空当前消息
    messages.value = [];

    // 延迟到首次发送再建会话，避免用户只是点击新建就产生空会话。
    sessionId.value = "";
    cacheActions?.setActiveSession?.("");

    console.log("🆕 已重置会话状态，等待用户发送第一条消息");
  };

  /**
   * 点击回复的【重新生成】按钮生成新的回复
   */
  const handleRegenerate = async (index: number) => {
    if (index < 0 || index >= messages.value.length) return;

    // 重新生成会替换后续回复，旧步骤需要同步失效。
    clearThinkingSteps();
    if (pendingClientTraceId) {
      pendingClientTraceId.value = createClientTraceId();
    }

    const deleted = await deleteMessagesFromIndex(index);
    if (!deleted) {
      ElMessage.error("重新生成失败，请重试");
      return;
    }

    // 重新发送最后一条用户消息
    try {
      await reload();
    } catch (error) {
      console.warn("AI 重新生成失败，已作为助手消息展示:", normalizeChatErrorMessage(error));
    }
  };

  /**
   * 点击【编辑】按钮
   * @param index
   * @param content
   */
  const editUserMsg = (index: number, content: string) => {
    editingMessageIndex.value = index;
    editingContent.value = content;
  };

  /**
   * 点击【取消】编辑按钮
   */
  const cancelEditUserMsg = () => {
    console.log("取消编辑...");
    editingMessageIndex.value = null;
    editingContent.value = "";
  };

  /**
   * 点击【重新发送】提交编辑，会删除后续的所有消息，并重新生成回复
   */
  const submitEditUserMsg = async (originalIndex: number) => {
    console.log("📝 提交编辑/重新发送", {
      originalIndex,
      editingContent: editingContent.value,
    });
    if (!editingContent.value.trim()) return;

    const editedContent = editingContent.value;

    cancelEditUserMsg();

    // 清空之前的思考步骤
    clearThinkingSteps();
    if (pendingClientTraceId) {
      pendingClientTraceId.value = createClientTraceId();
    }

    // 先删数据库后改前端，避免同一 sortIndex 重新保存时触发唯一约束冲突。
    const deleted = await deleteMessagesFromIndex(originalIndex, false, false);
    if (!deleted) {
      ElMessage.error("编辑失败，请重试");
      return;
    }

    // 🔑 第二步：更新前端用户消息内容
    const userMessage = messages.value[originalIndex];
    console.log("📝 更新用户消息", userMessage);

    if (userMessage) {
      userMessage.content = editedContent;
      userMessage.parts = [{ type: "text", text: editedContent }];

      // 用原 sortIndex 覆盖保存，保证编辑后的分叉仍出现在原问题位置。
      await saveMessage(
        sessionId.value,
        currentUserId.value,
        ChatRole.USER,
        editedContent,
        originalIndex,
      );
    }

    // 编辑问题后旧回答已不再对应当前上下文，只保留更新后的用户消息再生成。
    messages.value.splice(originalIndex + 1);
    cacheActions?.sync();

    try {
      await reload();
    } catch (error) {
      console.warn("AI 重新发送失败，已作为助手消息展示:", normalizeChatErrorMessage(error));
    }
  };

  /**
   * 删除用户消息及对应的 AI 回复
   */
  const deleteUserMsg = async (index: number) => {
    console.log("🗑️ 删除消息", { index, totalMessages: messages.value.length });

    // 获取要删除的消息 ID（用户消息 + AI 回复）
    const userMessage = messages.value[index];
    const aiMessage = messages.value[index + 1];

    if (!userMessage) {
      console.error("❌ 未找到用户消息");
      return;
    }

    const messageIdsToDelete: string[] = [userMessage.id];

    // 如果存在对应的 AI 回复，也加入删除列表
    if (aiMessage && aiMessage.role === ChatRole.ASSISTANT) {
      messageIdsToDelete.push(aiMessage.id);
    }

    try {
      // 🔗调用接口：删除问题（改用 POST 方法）
      const response: any = await $fetch("/api/message/messages-delete", {
        method: "POST",
        body: {
          messageIds: messageIdsToDelete, // 明确指定要删除的 ID
          userId: currentUserId.value,
        },
      });

      if (response.success) {
        console.log(`✅ 成功删除 ${response.deletedCount} 条消息`);

        // 前端同步删除消息（从后往前删，避免索引变化）
        const deleteCount = aiMessage ? 2 : 1;
        messages.value.splice(index, deleteCount);
        messages.value.length > 0 ? cacheActions?.sync() : cacheActions?.clear();

        ElMessage.success("删除成功");
      } else {
        throw new Error("删除失败");
      }
    } catch (error) {
      console.error("❌ 删除消息失败:", error);
      ElMessage.error("删除失败，请重试");
    }
  };

  /**
   * 🔑 封装：从指定索引删除所有后续消息（同步前端和后端）
   * @param fromIndex - 起始索引（包含该索引及之后的所有消息）
   * @param showSuccessMessage - 是否显示成功提示（默认 false）
   * @param deleteFrontend - 是否同步删除前端消息（默认 true）
   * @returns 是否删除成功
   */
  const deleteMessagesFromIndex = async (
    fromIndex: number,
    showSuccessMessage: boolean = false,
    deleteFrontend: boolean = true,
  ): Promise<boolean> => {
    if (!sessionId.value || !currentUserId.value) {
      console.warn("⚠️ 缺少 sessionId 或 userId，无法删除消息");
      return false;
    }

    try {
      // 1. 先调用后端接口删除数据库记录（改用 POST 方法）
      const deleteResponse: any = await $fetch(
        "/api/message/messages-from-index-delete",
        {
          method: "POST",
          body: {
            sessionId: sessionId.value,
            fromIndex: fromIndex,
            userId: currentUserId.value,
          },
        },
      );

      if (deleteResponse.success) {
        console.log(`✅ 已删除 ${deleteResponse.deletedCount} 条旧消息`);

        // 2. 根据参数决定是否删除前端消息
        if (deleteFrontend) {
          messages.value.splice(fromIndex);
          messages.value.length > 0 ? cacheActions?.sync() : cacheActions?.clear();
        }

        if (showSuccessMessage) {
          ElMessage.success("删除成功");
        }

        return true;
      } else {
        throw new Error("删除失败");
      }
    } catch (error) {
      console.error("❌ 删除消息失败:", error);
      if (showSuccessMessage) {
        ElMessage.error("删除失败，请重试");
      }
      return false;
    }
  };

  return {
    editingMessageIndex,
    editingContent,
    handleSend,
    stopGeneration,
    handleNewChat,
    handleRegenerate,
    editUserMsg,
    cancelEditUserMsg,
    submitEditUserMsg,
    deleteUserMsg,
    deleteMessagesFromIndex,
  };
}

function createClientTraceId(): string {
  const randomPart =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "")
      : Math.random().toString(36).slice(2);

  return `trace_${Date.now().toString(36)}_${randomPart.slice(0, 16)}`;
}
